import { useQuery } from "@tanstack/react-query";
import { getGainTVL, getTVL } from "@services/index";
import { useIdle } from "react-use";
import { toFixedWithoutRounding } from "@constants/common";
import { numberToCurrency } from "utils";
import { useMemo } from "react";
import useVaultPerformance from "./useVaultPerformance";
import {
  calculateSmartApy,
  STRATEGY_REPORTS_TOOLTIP,
} from "../utils/vaultApyUtils";
import {
  getLatestAvgApyFromReports,
  TokenType,
} from "../utils/strategyReportsUtils";

export type TVaultType =
  | "agETH"
  | "hgETH"
  | "sbUSD" // Stable vault (maps to rsETH in API)
  | "xgUSD"
  | "all";

const VAULT_DATA_MAP = {
  agETH: { apyKey: "agETH" as const, tvlKey: "agEthTvl" as const },
  hgETH: { apyKey: "hgETH" as const, tvlKey: "hgEthTvl" as const },
  sbUSD: { apyKey: "rsETH" as const, tvlKey: "sbUsdTvl" as const }, // API uses rsETH for sbUSD
  xgUSD: { apyKey: "xgUSD" as const, tvlKey: null },
} as const;

type TVaultDataMap = typeof VAULT_DATA_MAP;

/**
 * Format vault data with smart APY calculation
 */
const formatVaultData = (
  ethPrice: number,
  tvlValue: number | null | undefined,
  calculateEthValue: boolean = true,
  smartAPY?: string,
  apyTooltipInfo?: string,
  avgApyFromReports?: number | null
) => {
  const tvl = tvlValue ?? null;
  const ethStaked =
    calculateEthValue && tvl && ethPrice ? tvl / ethPrice : null;

  const finalApy = smartAPY || "-";
  const finalTooltipInfo = apyTooltipInfo || STRATEGY_REPORTS_TOOLTIP;

  return {
    apy: avgApyFromReports, // Keep avgAPY from reports for backward compatibility
    tvl,
    totalStaked: tvl, // Kept for backward compatibility
    ethStaked,
    totalStakedEth: ethStaked, // Kept for backward compatibility
    formattedApy: finalApy, // Now uses smartAPY by default
    smartAPY: finalApy, // New field
    apyTooltipInfo: finalTooltipInfo, // New field
    formattedTvl:
      tvl && !isNaN(Number(tvl))
        ? `${numberToCurrency(tvl.toFixed(2), 1)}`
        : "-",
    formattedETHStaked:
      ethStaked && !isNaN(Number(ethStaked))
        ? `${toFixedWithoutRounding(ethStaked.toString(), 2)} ETH`
        : "",
  };
};

// Core vault data shape (without loading states)
type TVaultDataCore = {
  apy: any;
  tvl: number | null;
  ethStaked: number | null;
  formattedApy: string; // Now uses smartAPY (backward compatible)
  formattedTvl: string;
  formattedETHStaked: string;
  smartAPY: string; // New: smart APY value (higher of timeframe APR or general APY)
  apyTooltipInfo: string; // New: tooltip info for smart APY
  // Deprecated fields (kept for backward compatibility)
  totalStaked: number | null; // Use tvl instead
  totalStakedEth: number | null; // Use ethStaked instead
};

// Individual vault data with loading states
type TVaultData = TVaultDataCore & {
  isLoadingApy: boolean; // Now includes performance loading state
  isLoadingTvl: boolean;
  isLoading: boolean;
};

// All vaults response
type TAllVaultsData = {
  isLoadingApy: boolean; // Now includes performance loading state
  isLoadingTvl: boolean;
  totalTvl: number;
  formattedTotalTvl: string;
  agETH: TVaultDataCore;
  hgETH: TVaultDataCore;
  sbUSD: TVaultDataCore;
  xgUSD: TVaultDataCore;
};

/**
 * Fetch vault APY and TVL data with smart APY calculation
 * Smart APY automatically selects the higher value between timeframe APR and general APY
 *
 * @param vault - Specific vault or "all" for all vaults
 */
// eslint-disable-next-line no-redeclare
export function useVaultTvlApy(vault: "all"): TAllVaultsData;
// eslint-disable-next-line no-redeclare
export function useVaultTvlApy(vault: Exclude<TVaultType, "all">): TVaultData;
// eslint-disable-next-line no-redeclare
export function useVaultTvlApy(vault: TVaultType = "all"): any {
  const isIdle = useIdle(150e3);

  // Fetch TVL data for all vaults
  const { data: tvlData, isLoading: isLoadingTvl } = useQuery({
    queryKey: ["gainTvl"],
    queryFn: async () => {
      const { data } = await getGainTVL();
      return data;
    },
    enabled: !isIdle,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Fetch rsETH TVL to calculate ETH price
  const { data: rsEthTvlData, isLoading: isLoadingRsEthTvl } = useQuery({
    queryKey: ["tvl"],
    queryFn: async () => {
      const { data } = await getTVL();
      return data;
    },
    enabled: !isIdle,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Calculate ETH price from rsETH TVL
  const ethPrice = useMemo(() => {
    if (rsEthTvlData?.value && rsEthTvlData?.usdTvl) {
      return rsEthTvlData.usdTvl / rsEthTvlData.value;
    }
    return 0;
  }, [rsEthTvlData]);

  // Get performance data for smart APY calculation
  // Hooks must be called unconditionally at the top level
  const agEthPerformance = useVaultPerformance("agETH" as TokenType);
  const hgEthPerformance = useVaultPerformance("hgETH" as TokenType);
  const sbUsdPerformance = useVaultPerformance("sbUSD" as TokenType);

  // isLoadingApy should be true if any performance data is loading
  const isLoadingApy = useMemo(() => {
    return (
      agEthPerformance.isLoading ||
      hgEthPerformance.isLoading ||
      sbUsdPerformance.isLoading
    );
  }, [
    agEthPerformance.isLoading,
    hgEthPerformance.isLoading,
    sbUsdPerformance.isLoading,
  ]);

  // Create a performance data map for easy lookup
  const performanceDataMap = useMemo(
    () => ({
      agETH: agEthPerformance,
      hgETH: hgEthPerformance,
      sbUSD: sbUsdPerformance,
    }),
    [agEthPerformance, hgEthPerformance, sbUsdPerformance]
  );

  // Helper to get smart APY for a vault using performance data and strategy reports
  const getSmartApyForVault = useMemo(() => {
    return (vaultType: TokenType) => {
      const performanceData = performanceDataMap[vaultType];
      if (!performanceData) {
        // Fallback for unsupported vault types
        return {
          smartAPY: "-",
          apyTooltipInfo: STRATEGY_REPORTS_TOOLTIP,
          avgApyFromReports: null,
        };
      }

      const selectedTimeframeApr =
        performanceData.chartData?.selectedTimeframeApr ?? null;
      const activeTimeframe = performanceData.activeTimeframe;
      const avgApyFromReports = getLatestAvgApyFromReports(vaultType);

      const smartApyResult = calculateSmartApy(
        avgApyFromReports,
        selectedTimeframeApr,
        activeTimeframe
      );

      return {
        ...smartApyResult,
        avgApyFromReports,
      };
    };
  }, [performanceDataMap]);

  // Process all vaults data
  const formattedData = useMemo(() => {
    if (vault.toLowerCase() === "all") {
      return processAllVaultsData(
        tvlData,
        ethPrice,
        isLoadingApy,
        isLoadingTvl,
        getSmartApyForVault
      );
    }

    return processSingleVaultData(
      vault as Exclude<TVaultType, "all">,
      tvlData,
      ethPrice,
      isLoadingApy,
      isLoadingTvl,
      isLoadingRsEthTvl,
      getSmartApyForVault
    );
  }, [
    vault,
    tvlData,
    ethPrice,
    isLoadingApy,
    isLoadingTvl,
    isLoadingRsEthTvl,
    getSmartApyForVault,
  ]);

  return formattedData;
}

/**
 * Process data for all vaults
 */
function processAllVaultsData(
  tvlData: any,
  ethPrice: number,
  isLoadingApy: boolean,
  isLoadingTvl: boolean,
  getSmartApyForVault: (vaultType: TokenType) => {
    smartAPY: string;
    apyTooltipInfo: string;
    avgApyFromReports: number | null;
  }
): TAllVaultsData {
  const totalTvl = tvlData?.tvl ?? 0;
  const formattedTotalTvl =
    totalTvl > 0
      ? `$${numberToCurrency(
          toFixedWithoutRounding(totalTvl.toString(), 2),
          2
        )}`
      : "-";

  // Calculate smart APY for each vault
  const agEthSmart = getSmartApyForVault("agETH");
  const hgEthSmart = getSmartApyForVault("hgETH");
  const sbUsdSmart = getSmartApyForVault("sbUSD");

  return {
    isLoadingApy,
    isLoadingTvl,
    totalTvl,
    formattedTotalTvl,
    agETH: formatVaultData(
      ethPrice,
      tvlData?.agEthTvl,
      true,
      agEthSmart.smartAPY,
      agEthSmart.apyTooltipInfo,
      agEthSmart.avgApyFromReports
    ),
    hgETH: formatVaultData(
      ethPrice,
      tvlData?.hgEthTvl,
      true,
      hgEthSmart.smartAPY,
      hgEthSmart.apyTooltipInfo,
      hgEthSmart.avgApyFromReports
    ),
    sbUSD: formatVaultData(
      ethPrice,
      tvlData?.sbUsdTvl,
      false,
      sbUsdSmart.smartAPY,
      sbUsdSmart.apyTooltipInfo,
      sbUsdSmart.avgApyFromReports
    ),
    xgUSD: formatVaultData(ethPrice, null, true),
  };
}

/**
 * Process data for a single vault
 */
function processSingleVaultData(
  vault: Exclude<TVaultType, "all">,
  tvlData: any,
  ethPrice: number,
  isLoadingApy: boolean,
  isLoadingTvl: boolean,
  isLoadingRsEthTvl: boolean,
  getSmartApyForVault: (vaultType: TokenType) => {
    smartAPY: string;
    apyTooltipInfo: string;
    avgApyFromReports: number | null;
  }
): TVaultData {
  const vaultKey = vault as keyof TVaultDataMap;
  const vaultConfig = VAULT_DATA_MAP[vaultKey];

  if (!vaultConfig) {
    // Fallback for unmapped vault types
    return {
      apy: null,
      tvl: null,
      totalStaked: null,
      ethStaked: null,
      totalStakedEth: null,
      formattedApy: "-",
      smartAPY: "-",
      apyTooltipInfo: STRATEGY_REPORTS_TOOLTIP,
      formattedTvl: "-",
      formattedETHStaked: "",
      isLoadingApy,
      isLoadingTvl,
      isLoading: isLoadingApy || isLoadingTvl || isLoadingRsEthTvl,
    };
  }

  // Extract values
  const tvlValue: number | null =
    vaultConfig.tvlKey && tvlData ? tvlData[vaultConfig.tvlKey] ?? null : null;
  const includeEthValue = vault.toLowerCase() !== "sbusd";

  // Map vault type to TokenType for performance hook
  const vaultTokenType: TokenType | null =
    vaultKey === "agETH"
      ? "agETH"
      : vaultKey === "hgETH"
      ? "hgETH"
      : vaultKey === "sbUSD"
      ? "sbUSD"
      : null;

  // Calculate smart APY
  const smartApyData = vaultTokenType
    ? getSmartApyForVault(vaultTokenType)
    : {
        smartAPY: "-",
        apyTooltipInfo: STRATEGY_REPORTS_TOOLTIP,
        avgApyFromReports: null,
      };

  // Format vault data
  const vaultData = formatVaultData(
    ethPrice,
    tvlValue,
    includeEthValue,
    smartApyData.smartAPY,
    smartApyData.apyTooltipInfo,
    smartApyData.avgApyFromReports
  );

  return {
    ...vaultData,
    isLoadingApy,
    isLoadingTvl,
    isLoading: isLoadingApy || isLoadingTvl || isLoadingRsEthTvl,
  };
}
